﻿import { useEffect, useMemo, useState } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, Layout,
  BlockStack, InlineStack, DataTable, Modal, TextField,
  Select, Spinner, Box, Tooltip, InlineGrid,
} from '@shopify/polaris';
import { TeamIcon, ClockIcon } from '@shopify/polaris-icons';
import { useTeam } from '../../hooks/useTeam';
import { INVITABLE_ROLES } from '../../api/team';
import type { TeamMember } from '../../api/team';
import { StatCard } from '../shared/StatCard';

// ── Helpers ────────────────────────────────────────────────────────────────────

function roleBadge(role: string) {
  const map: Record<string, 'success' | 'info' | 'attention' | 'new'> = {
    owner: 'success', admin: 'info', member: 'attention', viewer: 'new',
  };
  return <Badge tone={map[role] ?? 'new'}>{role}</Badge>;
}

function statusBadge(status: string) {
  const map: Record<string, 'success' | 'warning' | 'critical'> = {
    active: 'success', pending: 'warning', suspended: 'critical',
  };
  return <Badge tone={map[status] ?? 'info'}>{status}</Badge>;
}

// ── Invite Modal ───────────────────────────────────────────────────────────────

interface InviteModalProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onInvite: (data: { name: string; email: string; role: string }) => Promise<TeamMember | null>;
}

function InviteModal({ open, loading, onClose, onInvite }: InviteModalProps) {
  const [name,  setName]  = useState('');
  const [email, setEmail] = useState('');
  const [role,  setRole]  = useState('member');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    const e: Record<string, string> = {};
    if (!name.trim())   e.name  = 'Name is required.';
    if (!email.trim())  e.email = 'Email is required.';
    if (Object.keys(e).length) { setErrors(e); return; }

    const result = await onInvite({ name: name.trim(), email: email.trim(), role });
    if (result) {
      setName(''); setEmail(''); setRole('member'); setErrors({});
      onClose();
    }
  };

  const roleOptions = INVITABLE_ROLES.map((r) => ({ label: r.label, value: r.value }));
  const selectedRole = INVITABLE_ROLES.find((r) => r.value === role);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite Team Member"
      primaryAction={{ content: 'Send Invitation', loading, onAction: handleSubmit }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <TextField
            label="Full name"
            value={name}
            onChange={setName}
            autoComplete="off"
            error={errors.name}
          />
          <TextField
            label="Email address"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="off"
            error={errors.email}
          />
          <Select
            label="Role"
            options={roleOptions}
            value={role}
            onChange={setRole}
          />
          {selectedRole && (
            <Text as="p" variant="bodySm" tone="subdued">
              {selectedRole.description}
            </Text>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

export function TeamPanel() {
  const {
    members, loading, actionId, error,
    fetchMembers, invite, resendInvite, updateRole, suspend, reactivate, remove, setError,
  } = useTeam();

  const [showInvite,  setShowInvite]  = useState(false);
  const [removeId,    setRemoveId]    = useState<number | null>(null);
  const memberToRemove = members.find((m) => m.id === removeId);

  useEffect(() => { fetchMembers(); }, []);

  const activeCount = useMemo(() => members.filter((m) => m.is_active).length, [members]);
  const pendingCount = useMemo(() => members.filter((m) => m.is_pending).length, [members]);

  const rows = members.map((m) => [
    <BlockStack gap="100">
      <Text as="span" variant="bodyMd" fontWeight="semibold">{m.name}</Text>
      <Text as="span" variant="bodySm" tone="subdued">{m.email}</Text>
    </BlockStack>,
    roleBadge(m.role),
    statusBadge(m.status),
    m.invited_by ?? '—',
    m.accepted_at ? new Date(m.accepted_at).toLocaleDateString() : '—',
    <InlineStack gap="200" wrap={false}>
      {m.is_pending && (
        <Button
          size="slim"
          loading={actionId === m.id}
          disabled={actionId !== null && actionId !== m.id}
          onClick={() => resendInvite(m.id)}
        >
          Resend
        </Button>
      )}
      {!m.is_owner && !m.is_pending && (
        <>
          <Select
            label=""
            labelHidden
            options={INVITABLE_ROLES.map((r) => ({ label: r.label, value: r.value }))}
            value={m.role}
            onChange={(v) => updateRole(m.id, v)}
            disabled={actionId !== null}
          />
          {m.is_active ? (
            <Button
              size="slim"
              tone="critical"
              loading={actionId === m.id}
              disabled={actionId !== null && actionId !== m.id}
              onClick={() => suspend(m.id)}
            >
              Suspend
            </Button>
          ) : m.status === 'suspended' ? (
            <Button
              size="slim"
              loading={actionId === m.id}
              disabled={actionId !== null && actionId !== m.id}
              onClick={() => reactivate(m.id)}
            >
              Reactivate
            </Button>
          ) : null}
          <Button
            size="slim"
            tone="critical"
            variant="plain"
            loading={actionId === m.id}
            disabled={actionId !== null && actionId !== m.id}
            onClick={() => setRemoveId(m.id)}
          >
            Remove
          </Button>
        </>
      )}
      {m.is_owner && (
        <Tooltip content="The owner cannot be modified">
          <Text as="span" variant="bodySm" tone="subdued">Owner</Text>
        </Tooltip>
      )}
    </InlineStack>,
  ]);

  return (
    <Page fullWidth
      title="Team"
      subtitle="Manage who has access to your GrowthPilot AI dashboard"
      primaryAction={{
        content: 'Invite Member',
        onAction: () => setShowInvite(true),
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {members.length > 0 && (
            <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
              <StatCard label="Team Members" value={String(members.length)} icon={TeamIcon} iconColor="indigo" />
              <StatCard label="Active" value={String(activeCount)} icon={TeamIcon} iconColor="green" />
              <StatCard label="Pending Invites" value={String(pendingCount)} icon={ClockIcon} iconColor="orange" />
            </InlineGrid>
          )}

          {loading && members.length === 0 ? (
            <Card>
              <InlineStack align="center"><Spinner /></InlineStack>
            </Card>
          ) : members.length === 0 ? (
            <Card>
              <Box padding="800">
                <BlockStack gap="400" align="center" inlineAlign="center">
                  <Text as="h3" variant="headingMd">No team members yet</Text>
                  <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                    Invite colleagues to collaborate on your store's growth.
                  </Text>
                  <Button variant="primary" onClick={() => setShowInvite(true)}>
                    Invite your first member
                  </Button>
                </BlockStack>
              </Box>
            </Card>
          ) : (
            <Card padding="0">
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Member', 'Role', 'Status', 'Invited By', 'Joined', 'Actions']}
                rows={rows}
              />
            </Card>
          )}
          </BlockStack>
        </Layout.Section>
      </Layout>

      <InviteModal
        open={showInvite}
        loading={loading}
        onClose={() => setShowInvite(false)}
        onInvite={invite}
      />

      <Modal
        open={removeId !== null}
        onClose={() => setRemoveId(null)}
        title="Remove Team Member"
        primaryAction={{
          content: 'Yes, Remove',
          destructive: true,
          loading: actionId === removeId,
          onAction: async () => {
            if (removeId !== null) {
              await remove(removeId);
              setRemoveId(null);
            }
          },
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setRemoveId(null) }]}
      >
        <Modal.Section>
          <Text as="p" variant="bodyMd">
            Are you sure you want to remove <strong>{memberToRemove?.name}</strong> from your
            team? They will immediately lose access to the dashboard.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
